import { cacheKeys } from "@constants/cache-keys";
import { useAuthContext } from "@features/auth/contexts/AuthContext";
import { apiAxios } from "@helpers/lib/axios";
import { useAppStore } from "@hooks/useAppStore";
import { API_ENDPOINTS } from "@memorang/constants/urls";
import { MigrationDialog } from "@memorang/ui/index";
import { useQuery } from "@tanstack/react-query";
import { createContext, useCallback, useEffect } from "react";

const REFRESH_INTERVAL = 3000;

const isDev = process.env.NODE_ENV === "development";

const getMigrationUrls = (tenantName: string, numericId: number) => {
	switch (tenantName.toLowerCase()) {
		case "mcgrawhill":
			return {
				post: API_ENDPOINTS.USER_MIGRATED_STATUS,
				get: API_ENDPOINTS.FETCH_MIGRATION_STATUS(numericId),
			};
		default:
			return null;
	}
};

type MigrationContextType = {
	isMigrated: boolean | undefined;
};

const MigrationContext = createContext<MigrationContextType | null>(null);

type Props = {
	children: React.ReactNode;
};

export const MigrationProvider = ({ children }: Props) => {
	const { userLogicalId, userPublicMetadataId } = useAuthContext();
	const tenant = useAppStore((store) => store.tenant);
	const migrationUrls = getMigrationUrls(
		tenant?.name ?? "",
		userPublicMetadataId!,
	);

	const fetchMigrationStatus = useCallback(async () => {
		if (!(migrationUrls && userPublicMetadataId)) {
			return;
		}
		const response = await apiAxios.get(migrationUrls.get);
		return response.data.data;
	}, [migrationUrls, userPublicMetadataId]);

	const triggerMigration = useCallback(async () => {
		const hasAlreadyMigrated = await fetchMigrationStatus();
		if (hasAlreadyMigrated || !migrationUrls || !userLogicalId) {
			return;
		}
		const url = migrationUrls.post;
		if (!url) {
			return;
		}
		const response = await apiAxios.post(url, {
			userId: userLogicalId,
		});
		const data = await response.data.data;
		return data;
	}, [fetchMigrationStatus, userLogicalId, migrationUrls]);

	const { data: isMigrated } = useQuery({
		queryKey: [cacheKeys.migration, userLogicalId],
		queryFn: fetchMigrationStatus,
		refetchInterval: ({ state }) => {
			return state?.data === true || isDev ? false : REFRESH_INTERVAL;
		},
		staleTime: 0,
		enabled: !!userLogicalId && !!migrationUrls && !!userPublicMetadataId,
		// enabled: false,
	});

	useEffect(() => {
		if (isMigrated === false && userPublicMetadataId) {
			triggerMigration();
		}
	}, [isMigrated, triggerMigration, userPublicMetadataId]);

	if (isMigrated === false) {
		return <MigrationDialog isVisible={true} />;
	}

	return (
		<MigrationContext.Provider value={{ isMigrated }}>
			{children}
		</MigrationContext.Provider>
	);
};
